import { DropDownList } from "@progress/kendo-react-dropdowns";
import { NumericTextBox, Switch, Checkbox } from "@progress/kendo-react-inputs";
import {
    setBufferDistance,
    setBufferUnits,
    setBufferState,
    setActiveIdentifyTool,
    setBufferFeature,
    setFeaturesToBuffer,
    setBufferToDrawingLayer,
} from "../../../../redux/identifySlice";
import React, { useContext, useEffect, useRef } from "react";
import MapContext from "../../../MapContext";
import { useSelector, useDispatch } from "react-redux";
import "../Results.css";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector";
import { Button, ButtonGroup, Toolbar, ToolbarItem } from "@progress/kendo-react-buttons";
import { DragBox } from "ol/interaction";
import GeoJSON from "ol/format/GeoJSON";
import { Point } from "ol/geom.js";
import { setResultsData } from "../../../../redux/resultsSlice";
import { Draw } from "ol/interaction.js";
import {
    doesIntersect,
    bufferGeometry,
    addGeometryToSource,
    getFeatureWithId,
    getMapLayerById,
} from "../../../utils/mapUtils";
import union from "@turf/union";
import { inAndOut } from "ol/easing";
import { Style, Stroke, Fill } from "ol/style";
import { setActiveTool } from "../../../../redux/toolSelectSlice";
import { ImTarget } from "react-icons/im";
import { TbVector } from "react-icons/tb";
import { TfiLayoutLineSolid } from "react-icons/tfi";

const IdentifyOptions = () => {
    const { map } = useContext(MapContext);

    const bufferDrawRef = useRef();
    const vectorDrawRef = useRef();
    const drawRef = useRef();
    const dragbox = useRef();

    const vectorLayers = useSelector((state) => state.configData.vectorLayerProperties);

    const activeIdentifyTool = useSelector(
        (state) => state.identifyOptions.activeIdentifyTool
    );
    const unit = useSelector((state) => state.identifyOptions.units);
    const distance = useSelector((state) => state.identifyOptions.distance);
    const units = useSelector((state) => state.configData.mapConfig.map.units);

    const activeTool = useSelector((state) => state.toolSelect.tool);
    // state of buffer and reference (boolean)
    const bufferRef = useRef();
    const buffer = useSelector((state) => state.identifyOptions.buffer);
    // state of selected unit and distance to buffer
    // ref of units converted to map units
    const mapUnitsToBuffer = useRef();
    const bufferUnits = useSelector((state) => state.identifyOptions.units);
    const bufferDistance = useSelector((state) => state.identifyOptions.distance);
    /*
  Units will need to be handled better
  */
    const mapUnits = units.includes("Feet") ? "Feet" : "Meters";

    // state containing layer and feature ids of features to buffer
    // [{layerId, [featureIds]}, {layerId...}]
    const featuresToBuffer = useSelector(
        (state) => state.identifyOptions.featuresToBuffer
    );

    // boolean to initiate buffering of features
    const bufferFeature = useSelector((state) => state.identifyOptions.bufferFeature);

    // boolean to write buffer layer to drawing layer
    const writeToDrawing = useSelector(
        (state) => state.identifyOptions.bufferToDrawingLayer
    );

    const dispatch = useDispatch();

    const unitChange = (e) => {
        dispatch(setBufferUnits(e.value));
    };

    const handleDistanceChange = (e) => {
        dispatch(setBufferDistance(e.value));
    };

    const handleBufferState = (e) => {
        bufferDrawRef.current.clear();
        dispatch(setBufferState(e.value));
    };

    const handleClick = () => {
        dispatch(setActiveIdentifyTool("click"));
    };

    const handleBoundingBox = () => {
        dispatch(setActiveIdentifyTool("bounding box"));
    };

    const handleLine = () => {
        dispatch(setActiveIdentifyTool("LineString"));
    };

    const handleBufferFeatures = () => {
        if (activeTool !== "identify") {
            dispatch(setActiveTool("identify"));
        }
        dispatch(setBufferFeature(true));
    };

    // For identify on click
    const identifyOnClick = (e) => {
        const viewPortPixel = map.getEventPixel(e.originalEvent);
        const mapPixel = map.getCoordinateFromPixel(viewPortPixel);
        let geometry;
        const point = new Point(mapPixel);
        // Default = ~3px buffer
        if (bufferRef.current === true) {
            geometry = bufferGeometry(
                point,
                bufferDrawRef.current,
                mapUnitsToBuffer.current
            );
            // Show buffered shape
            addGeometryToSource(bufferDrawRef.current, geometry);
        } else {
            const threePxBuffer = map.getView().getResolution() * 3;
            geometry = bufferGeometry(point, bufferDrawRef.current, threePxBuffer);
        }
        setIntersectingFeatures(geometry);
    };
    // Identify buffered feature(s)
    const identifyBufferedFeature = (geometry) => {
        setIntersectingFeatures(geometry);
    };

    // For bounding box identify
    const boundingBoxIdentify = () => {
        let geometry = dragbox.current.getGeometry();
        if (bufferRef.current === true) {
            geometry = bufferGeometry(
                geometry,
                bufferDrawRef.current,
                mapUnitsToBuffer.current
            );
            addGeometryToSource(bufferDrawRef.current, geometry);
        }
        setIntersectingFeatures(geometry);
    };

    // Take a geometry and tests if all 'Identifiable' layers on the map intersect
    // sets intersecting features to state
    const setIntersectingFeatures = (geometry) => {
        const extent = geometry.getExtent();
        const allLayers = map.getAllLayers();
        const layers = [];
        allLayers.forEach((layer) => {
            const isVisible = layer.getVisible();
            const layerId = layer.values_.Id;
            // match layer from map to saved config data in state
            const matchedLayer = vectorLayers.find((mapLayer) => mapLayer.Id === layerId);
            // check to see if it was in state and is identifiable
            if (matchedLayer && matchedLayer.Identifiable === true && isVisible) {
                // create array for storing the id of each feature in extent
                const featureIds = [];
                const featuresInExtent = layer.getSource().getFeaturesInExtent(extent);
                featuresInExtent.forEach((feature) => {
                    const intersection = doesIntersect(feature, geometry); // returns boolean
                    // if there is an intersection push the feature id to the array
                    if (intersection === true) {
                        featureIds.push(feature.id_);
                    }
                });
                // if there were intersections between features in layer and test geometry
                // create object with data from matched layer in state
                // with an array of the features that intersected
                if (featureIds.length > 0) {
                    const layerObj = {
                        DisplayName: matchedLayer.DisplayName,
                        FeatureLabel: matchedLayer.FeatureLabel,
                        FeatureDescription: matchedLayer.FeatureDescription,
                        Id: matchedLayer.Id,
                        Features: featureIds,
                    };
                    layers.push(layerObj);
                }
            }
        });
        dispatch(setResultsData(layers));
    };

    const getFeaturesIntersectingLine = (e) => {
        const feature = e.feature;
        let geometry = feature.getGeometry();
        if (bufferRef.current === true) {
            geometry = bufferGeometry(
                geometry,
                bufferDrawRef.current,
                mapUnitsToBuffer.current
            );
            addGeometryToSource(bufferDrawRef.current, geometry);
        }
        setIntersectingFeatures(geometry);
    };

    // Runs after user selects buffer parameters and hits go (setBufferFeature(true))
    useEffect(() => {
        if (bufferFeature === true) {
            const format = new GeoJSON();
            let bufferedGeometry;
            try {
                featuresToBuffer.forEach((layer) => {
                    // If results layer is geocode, take featureId and create Point geometry
                    if (layer.layerId === "ArcGIS Geocoder") {
                        layer.featureIds.forEach((feature) => {
                            const olGeometry = new Point(feature);
                            const bufferedGeom = bufferGeometry(
                                olGeometry,
                                bufferDrawRef.current,
                                mapUnitsToBuffer.current
                            );
                            const geomObject = format.writeGeometryObject(bufferedGeom);
                            if (bufferedGeometry === undefined) {
                                bufferedGeometry = geomObject;
                            } else {
                                bufferedGeometry = union(geomObject, bufferedGeometry);
                            }
                        });
                    } else {
                        layer.featureIds.forEach((feature) => {
                            // Get geometry from map to buffer
                            const olFeature = getFeatureWithId(
                                map,
                                layer.layerId,
                                feature
                            );
                            const olGeometry = olFeature.getGeometry();
                            const bufferedGeom = bufferGeometry(
                                olGeometry,
                                bufferDrawRef.current,
                                mapUnitsToBuffer.current
                            );
                            const geomObject = format.writeGeometryObject(bufferedGeom);
                            if (bufferedGeometry === undefined) {
                                bufferedGeometry = geomObject;
                            } else {
                                bufferedGeometry = union(geomObject, bufferedGeometry);
                            }
                        });
                    }
                });
                const unionOlFeature = format.readFeature(bufferedGeometry);
                const unionOlGeom = unionOlFeature.getGeometry();
                const extent = unionOlGeom.getExtent();

                addGeometryToSource(bufferDrawRef.current, unionOlGeom);
                identifyBufferedFeature(unionOlGeom);
                if (writeToDrawing === true) {
                    const vectorLayer = getMapLayerById(map, "drawLayer");
                    const vectorSource = vectorLayer.getSource();
                    addGeometryToSource(vectorSource, unionOlGeom);
                }
                map.getView().fit(extent, {
                    padding: [30, 30, 30, 30],
                    duration: 750,
                    easing: inAndOut,
                });
            } catch (error) {
                console.error(error);
                console.log("handle buffering error");
            }

            dispatch(setBufferToDrawingLayer(false));
            dispatch(setFeaturesToBuffer([]));
            dispatch(setBufferFeature(false));
        }
    }, [bufferFeature]);

    // Update buffer ref (boolean)
    useEffect(() => {
        bufferRef.current = buffer;
    }, [buffer]);

    // Handle unit changes, update reference
    useEffect(() => {
        let num;
        if (mapUnits === bufferUnits) {
            mapUnitsToBuffer.current = bufferDistance;
        } else {
            if (mapUnits === "Feet") {
                if (bufferUnits === "Yards") {
                    num = bufferDistance * 3;
                } else if (bufferUnits === "Meters") {
                    num = bufferDistance * 3.28084;
                } else if (bufferUnits === "Kilometers") {
                    num = bufferDistance * 3.28084 * 1000;
                } else if (bufferUnits === "Miles") {
                    num = bufferDistance * 5280;
                } else if (bufferUnits === "Nautical Miles") {
                    num = bufferDistance * 6076.12;
                }
                mapUnitsToBuffer.current = num;
            }
        }
    }, [bufferUnits, bufferDistance]);

    // Handle changing of active identify tool
    useEffect(() => {
        if (!activeIdentifyTool) return;
        // activate click on UI
        if (activeIdentifyTool === "click") {
            map.on("click", identifyOnClick);
        }
        // activate bounding box on UI
        else if (activeIdentifyTool === "bounding box") {
            map.on("click", identifyOnClick);
            dragbox.current.setActive(true);
        }
        // activate line on UI
        else if (activeIdentifyTool === "LineString") {
            drawRef.current = new Draw({
                source: vectorDrawRef.current,
                type: "LineString",
                style: drawStyle,
            });
            map.addInteraction(drawRef.current);
            drawRef.current.on("drawend", (e) => {
                getFeaturesIntersectingLine(e);
                // Feature is not added on drawend event
                vectorDrawRef.current.clear();
            });
        }
        // runs when activeIdentifyTool is changed
        // clears active tool before code in beginning of useEffect activates new tool
        return () => {
            map.un("click", identifyOnClick);
            dragbox.current.setActive(false);
            map.removeInteraction(drawRef.current);
            // clear vector layers
            bufferDrawRef.current.clear();
            vectorDrawRef.current.clear();
        };
    }, [activeIdentifyTool, activeTool]);

    // Drag box is an interaction so must be added and removed
    // Assuming it's quicker, drag box (Bounding Box) is added
    // and then removed when entire component mounts and unmounts
    // and in between uses its active state boolean value is changed
    //
    // Add vector layer to draw to and remove on unmount
    //
    // Add buffer layer to draw to and remove on unmount
    useEffect(() => {
        dispatch(setActiveIdentifyTool("bounding box"));

        // add vector layer to draw to
        vectorDrawRef.current = new VectorSource();
        const vectorLayer = new VectorLayer({
            source: vectorDrawRef.current,
            style: highlightStyle,
        });
        map.addLayer(vectorLayer);
        vectorDrawRef.current.on("addfeature", () => {
            vectorDrawRef.current.clear();
        });

        // add vector layer to buffer
        bufferDrawRef.current = new VectorSource();
        const vectorBuffer = new VectorLayer({
            source: bufferDrawRef.current,
            style: bufferStyle,
        });
        map.addLayer(vectorBuffer);

        // initialize drag box
        dragbox.current = new DragBox();
        map.addInteraction(dragbox.current);
        dragbox.current.setActive(false);
        dragbox.current.on("boxend", (e) => {
            boundingBoxIdentify(e);
        });
        return () => {
            dispatch(setActiveIdentifyTool(undefined));
            map.removeLayer(vectorLayer);
            map.removeInteraction(dragbox.current);
            map.removeLayer(vectorBuffer);
        };
    }, []);

    return (
        <>
            <div className="title">
                <h3>Identify</h3>
            </div>
            <div className="identify-option">
                <h4>Tools</h4>
                <div className="identify-tools">
                    <Toolbar className="identify-toolbar">
                        <ToolbarItem>
                            <ButtonGroup>
                                <Button
                                    selected={
                                        activeIdentifyTool === "click" ? true : false
                                    }
                                    togglable={true}
                                    onClick={handleClick}
                                    className="identify-tool">
                                    <ImTarget />
                                    Point
                                </Button>
                                <Button
                                    selected={
                                        activeIdentifyTool === "bounding box"
                                            ? true
                                            : false
                                    }
                                    togglable={true}
                                    onClick={handleBoundingBox}
                                    className="identify-tool">
                                    <TbVector />
                                    Rectangle
                                </Button>
                                <Button
                                    selected={
                                        activeIdentifyTool === "LineString" ? true : false
                                    }
                                    togglable={true}
                                    onClick={handleLine}
                                    className="identify-tool">
                                    <TfiLayoutLineSolid />
                                    Line
                                </Button>
                            </ButtonGroup>
                        </ToolbarItem>
                    </Toolbar>
                </div>
            </div>
            <div className="identify-option">
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}>
                    <h4>Buffer</h4>
                    {featuresToBuffer.length > 0 ? (
                        <div />
                    ) : (
                        <Switch onChange={handleBufferState} />
                    )}
                </div>
                {buffer || featuresToBuffer.length > 0 ? (
                    <>
                        <div style={{ padding: "8px" }}>
                            <div>Unit</div>
                            <DropDownList
                                style={{ width: "200px" }}
                                data={Units}
                                value={unit}
                                onChange={unitChange}
                            />
                        </div>
                        <div style={{ padding: "8px" }}>
                            <div>Distance</div>
                            <NumericTextBox
                                style={{ width: "200px" }}
                                //   placeholder={bufferDistance}
                                value={distance}
                                onChange={handleDistanceChange}
                            />
                        </div>
                    </>
                ) : (
                    <div />
                )}
            </div>
            {featuresToBuffer.length > 0 ? (
                <>
                    <Button onClick={handleBufferFeatures} style={{ marginLeft: "8px" }}>
                        Buffer Feature(s)
                    </Button>
                    <div
                        style={{ display: "flex", flexDirection: "row", padding: "8px" }}>
                        <Checkbox
                            onClick={() => dispatch(setBufferToDrawingLayer(true))}
                            defaultChecked={writeToDrawing}
                        />
                        <div style={{ paddingLeft: "5px" }}>
                            Add feature to drawing layer
                        </div>
                    </div>
                </>
            ) : (
                <div />
            )}
        </>
    );
};

export default IdentifyOptions;

const Units = ["Feet", "Meters", "Yards", "Kilometers", "Miles", "Nautical Miles"];

const highlightStyle = new Style({
    zIndex: 10000000,
    stroke: new Stroke({
        color: "blue",
        width: 2,
    }),
});

const drawStyle = new Style({
    zIndex: 10000000,
    stroke: new Stroke({
        color: "blue",
        width: 2,
    }),
});

const bufferStyle = new Style({
    stroke: new Stroke({
        color: "red",
        width: 2,
    }),
    fill: new Fill({
        color: "#ffccc244",
    }),
});
